import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBCardTitle,
  MDBInput,
  MDBPagination,
  MDBPaginationItem,
  MDBPaginationLink
} from 'mdb-react-ui-kit';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from "react-i18next";
import useDebounce from '../../../utils/debounce';
import { captureError } from '../../../utils/sentryHelpers';

const client = generateClient();
const ITEMS_PER_PAGE = 50;

export default function AdminAllProducts() {
  const { t, i18n } = useTranslation();
  const [productList, setProductList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    fetchProducts();
  }, [currentPage]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      handleSearch();
    } else {
      fetchProducts();
    }
  }, [debouncedSearchTerm]);

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      // First search for companies matching the search term
      const companiesResponse = await client.graphql({
        query: /* GraphQL */ `
          query SearchCompanies(
            $filter: SearchableCompanyFilterInput
            $limit: Int
          ) {
            searchCompanies(
              filter: $filter
              limit: $limit
            ) {
              items {
                id
                name
              }
            }
          }
        `,
        variables: {
          filter: {
            name: {
              matchPhrasePrefix: debouncedSearchTerm
            }
          },
          limit: 100
        }
      });
  
      const matchingCompanyIds = companiesResponse.data.searchCompanies.items.map(company => company.id);
  
      // Search for products with nested name search
      const response = await client.graphql({
        query: /* GraphQL */ `
          query SearchCMSProducts(
            $filter: SearchableCMSProductFilterInput
            $limit: Int
            $from: Int
          ) {
            searchCMSProducts(
              filter: $filter
              limit: $limit
              from: $from
            ) {
              items {
                id
                name {
                  language
                  text
                }
                status
                company {
                  id
                  name
                  image {
                    main {
                      fileName
                      alt
                    }
                  }
                }
                employee {
                  id
                  image {
                    fileName
                    alt
                  }
                  user {
                    firstName
                    lastName
                  }
                }
                createdAt
                updatedAt
              }
              total
            }
          }
        `,
        variables: {
            
          filter: {
            or: [
                // {
                //     name: {
                //         text: {
                //           match: debouncedSearchTerm
                //         }
                //       }
                //   },
              ...(matchingCompanyIds.length > 0 ? [{
                companyProductsId: {
                  eq: matchingCompanyIds[0]
                }
              }] : [])
            ]
          },
          limit: ITEMS_PER_PAGE,
          from: (currentPage - 1) * ITEMS_PER_PAGE
        }
      });
      // Sorted by date
      setProductList(response.data.searchCMSProducts.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));

      setTotalProducts(response.data.searchCMSProducts.total);
    } catch (error) {
      console.error('Error searching products:', error);
      captureError(error, {
        feature: 'company-product-management-admin',
        operation: 'search-products',
        context: { searchTerm: debouncedSearchTerm, currentPage }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query SearchCMSProducts(
            $limit: Int
            $from: Int
            $sort: [SearchableCMSProductSortInput]
          ) {
            searchCMSProducts(
              limit: $limit
              from: $from
              sort: $sort
            ) {
              items {
                id
                name {
                  language
                  text
                }
                status
                company {
                  id
                  name
                  image {
                    main {
                      fileName
                      alt
                    }
                  }
                }
                employee {
                  id
                  image {
                    fileName
                    alt
                  }
                  user {
                    firstName
                    lastName
                  }
                }
                createdAt
                updatedAt
              }
              total
            }
          }
        `,
        variables: {
          limit: ITEMS_PER_PAGE,
          from: (currentPage - 1) * ITEMS_PER_PAGE,
          sort: {
            field: 'createdAt',
            direction: 'desc'
          }
        }
      });

      setProductList(response.data.searchCMSProducts.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setTotalProducts(response.data.searchCMSProducts.total);
    } catch (error) {
      console.error('Error fetching products:', error);
      captureError(error, {
        feature: 'company-product-management-admin',
        operation: 'fetch-products',
        context: { currentPage }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProduct = async () => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation DeleteCMSProduct($input: DeleteCMSProductInput!) {
            deleteCMSProduct(input: $input) {
              id
            }
          }
        `,
        variables: { input: { id: selectedProduct } }
      });
      setDeleteModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      captureError(error, {
        feature: 'company-product-management-admin',
        operation: 'delete-product',
        context: { productId: selectedProduct }
      });
    }
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
  };

  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

  return (
    <>
      <MDBCard>
        <MDBCardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <MDBCardTitle>{t("Company Products")}</MDBCardTitle>
            <MDBInput
              type='text'
              label={t('Search by product or company name')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              contrast
              style={{ maxWidth: '300px' }}
            />
          </div>

          {isLoading ? (
            <div className="text-center">
              <MDBIcon fas icon="spinner" spin /> {t("Loading")}...
            </div>
          ) : (
            <div className="table-responsive">
            <MDBTable>
              <MDBTableHead>
                <tr>
                  <th>{t("Date")}</th>
                  <th>{t("Company")}</th>
                  <th>{t("Name")}</th>
                  <th>{t("Employee")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {productList.map(product => (
                  <tr key={product.id}>
                    <td>
                      {new Date(product.createdAt).toLocaleDateString('de-DE', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: '2-digit' 
                      })}
                      {new Date(product.createdAt).toLocaleDateString() !== 
                       new Date(product.updatedAt).toLocaleDateString() && (
                        <>
                          <br />
                          <span className="text-success" style={{fontSize: "smaller"}}>
                            {new Date(product.updatedAt).toLocaleDateString('de-DE', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: '2-digit' 
                            })}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        {product.company?.image?.main?.fileName ? (
                          <img
                            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${product.company.image.main.fileName}`}
                            className="rounded-circle border"
                            style={{ height: "50px", width: "50px", objectFit: "contain" }}
                            alt={product.company.name}
                          />
                        ) : (
                          <MDBIcon 
                            className="rounded-circle border"
                            style={{ 
                              height: "50px", 
                              width: "50px", 
                              textAlign: "center", 
                              paddingTop: "12px", 
                              fontSize: "20px" 
                            }}
                            fas 
                            icon="building" 
                          />
                        )}
                        <div className='ms-3'>
                          <p className='fw-bold mb-1'>{product.company?.name}</p>
                        </div>
                      </div>
                    </td>
                    <td>{product.name?.find(n => n.language === i18n.language)?.text}</td>
                    <td>
                      {product.employee && (
                        product.employee?.image?.fileName ? (
                          <img
                            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${product.employee.image.fileName}`}
                            className="rounded-circle border"
                            style={{ height: "50px", width: "50px", objectFit: "cover" }}
                            alt={`${product.employee.user.firstName} ${product.employee.user.lastName}`}
                          />
                        ) : (
                          <MDBIcon
                            className="rounded-circle border"
                            style={{
                              height: "50px",
                              width: "50px",
                              textAlign: "center",
                              paddingTop: "12px",
                              fontSize: "20px"
                            }}
                            fas
                            icon="user-alt"
                          />
                        )
                      )}
                    </td>
                    <td>
                    {product.approved ? (
                        <MDBIcon fas icon="check" className="text-success" />
                      ) : (
                        <MDBIcon fas icon="clock" className="text-warning" />
                      )}
                      {' '}
                      {product.status === "created" ? null : t(product.status)}
                      </td>
                    <td>
                      <Link to={`/admin/company-product/${product.company?.id}/product/${product.id}`}>
                        <MDBBtn className='mx-2 my-0 py-0' color='link'>
                          {t("Edit")}
                        </MDBBtn>
                      </Link>
                      <MDBBtn
                        className='mx-2 my-0 py-0'
                        color='link'
                        style={{color: "red"}}
                        onClick={() => {
                          setSelectedProduct(product.id);
                          toggleDeleteModal();
                        }}
                      >
                        {t("Delete")}
                      </MDBBtn>
                    </td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
            </div>
          )}

          {totalPages > 1 && (
            <MDBPagination className="mb-0 justify-content-center">
              <MDBPaginationItem disabled={currentPage === 1}>
                <MDBPaginationLink 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  tabIndex={-1}
                   className="text-dark"
                >
                  {t("Previous")}
                </MDBPaginationLink>
              </MDBPaginationItem>

              {[...Array(totalPages)].map((_, index) => (
                <MDBPaginationItem key={index + 1} active={currentPage === index + 1}>
                  <MDBPaginationLink className="text-dark" onClick={() => setCurrentPage(index + 1)}>
                    {index + 1}
                  </MDBPaginationLink>
                </MDBPaginationItem>
              ))}

              <MDBPaginationItem disabled={currentPage === totalPages}>
                <MDBPaginationLink
                 className="text-dark" 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                >
                  {t("Next")}
                </MDBPaginationLink>
              </MDBPaginationItem>
            </MDBPagination>
          )}
        </MDBCardBody>
      </MDBCard>

      <MDBModal open={deleteModal} onClose={toggleDeleteModal} staticBackdrop>
        <MDBModalDialog centered size="sm">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Delete Product")}</MDBModalTitle>
              <MDBBtn className='btn-close' color='none' onClick={toggleDeleteModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              {t("Are you sure you want to delete this product?")}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color='secondary' onClick={toggleDeleteModal}>
                {t("Cancel")}
              </MDBBtn>
              <MDBBtn color='danger' onClick={deleteProduct}>
                {t("Delete")}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  );
}